import { getActiveWorkspaceSlug } from "../../../utils/workspaceStorage";

const STORAGE_KEY = "xpro4.chatMessageDrafts.v1";
const MAX_DRAFTS = 200;

export const CHAT_DRAFT_MODES = {
  CLIENT: "client",
  INTERNAL: "internal",
};

const EMPTY_DRAFT = {
  text: "",
  transferTo: null,
  selectedTemplate: null,
  pendingVideoInviteButton: null,
};

function getWorkspaceSlug(company) {
  if (company) return String(company).trim().toLowerCase();
  try {
    return String(getActiveWorkspaceSlug() || "").trim().toLowerCase() || "default";
  } catch {
    return "default";
  }
}

function normalizeDraftMode(mode) {
  if (mode === CHAT_DRAFT_MODES.INTERNAL || mode === "internal") {
    return CHAT_DRAFT_MODES.INTERNAL;
  }
  return CHAT_DRAFT_MODES.CLIENT;
}

function buildDraftKey({ conversationId, userId, company, mode }) {
  const uid = userId != null ? String(userId) : "anon";
  const ws = getWorkspaceSlug(company);
  const base = `${uid}:${ws}:${Number(conversationId)}`;
  if (mode == null || mode === "") return base;
  return `${base}:${normalizeDraftMode(mode)}`;
}

function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeStore(store) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* quota / private mode */
  }
}

function pruneStore(store) {
  const entries = Object.entries(store);
  if (entries.length <= MAX_DRAFTS) return store;
  entries.sort((a, b) => (b[1]?.updatedAt || 0) - (a[1]?.updatedAt || 0));
  return Object.fromEntries(entries.slice(0, MAX_DRAFTS));
}

/** Normaliza payload de transferência salvo no rascunho. */
export function normalizeDraftTransferTo(value) {
  if (!value || typeof value !== "object") return null;
  const type = value.type === "agent" ? "agent" : value.type === "user" ? "user" : null;
  if (!type) return null;
  if (value.id == null || value.id === "") return null;
  const label = String(value.label || "").trim();
  if (!label) return null;
  return { type, id: value.id, label };
}

/** Template WhatsApp selecionado — precisa do id no envio fora da janela 24h. */
export function normalizeDraftSelectedTemplate(value) {
  if (!value || typeof value !== "object") return null;
  if (value.id == null || value.id === "") return null;
  const out = { id: value.id };
  if (value.name != null && String(value.name).trim()) {
    out.name = String(value.name).trim();
  }
  if (value.preview != null && String(value.preview).trim()) {
    out.preview = String(value.preview).trim();
  }
  // raw pode ser grande; só guarda se for objeto serializável pequeno o bastante.
  if (value.raw != null && typeof value.raw === "object") {
    try {
      const rawJson = JSON.stringify(value.raw);
      if (rawJson.length <= 8000) out.raw = value.raw;
    } catch {
      /* ignore */
    }
  }
  return out;
}

/** Botão interativo do convite de vídeo WhatsApp. */
export function normalizeDraftPendingVideoInvite(value) {
  if (!value || typeof value !== "object") return null;
  const buttonMessage = String(value.buttonMessage || "").trim();
  const joinUrl = String(value.joinUrl || "").trim();
  if (!buttonMessage || !joinUrl) return null;
  return { buttonMessage, joinUrl };
}

function draftHasPayload(draft) {
  return Boolean(
    (draft.text && String(draft.text).trim()) ||
      draft.transferTo ||
      draft.selectedTemplate ||
      draft.pendingVideoInviteButton
  );
}

function readEntry(store, key) {
  const entry = store[key];
  if (!entry || typeof entry !== "object") {
    return { ...EMPTY_DRAFT };
  }
  return {
    text: typeof entry.text === "string" ? entry.text : "",
    transferTo: normalizeDraftTransferTo(entry.transferTo),
    selectedTemplate: normalizeDraftSelectedTemplate(entry.selectedTemplate),
    pendingVideoInviteButton: normalizeDraftPendingVideoInvite(
      entry.pendingVideoInviteButton
    ),
  };
}

/**
 * Se o texto do rascunho ainda tem `@Nome …`, reconstrói transferTo a partir das
 * opções de menção (fallback quando o rascunho legado só tinha texto).
 */
export function resolveTransferToFromMentionText(plainText, options) {
  const plain = String(plainText || "").replace(/\u00a0/g, " ");
  const lastAt = plain.lastIndexOf("@");
  if (lastAt < 0) return null;
  const afterAt = plain.slice(lastAt + 1);
  const list = Array.isArray(options) ? options : [];
  let best = null;
  for (const opt of list) {
    const label = String(opt?.label || "").trim();
    if (!label) continue;
    const still =
      afterAt === label || afterAt === `${label} ` || afterAt.startsWith(`${label} `);
    if (!still) continue;
    if (!best || label.length > best.label.length) {
      best = normalizeDraftTransferTo({
        type: opt.type,
        id: opt.id,
        label,
      });
    }
  }
  return best;
}

export function loadChatComposerDraft(conversationId, { userId, company, mode } = {}) {
  if (!conversationId) return { ...EMPTY_DRAFT };
  const store = readStore();
  const legacyKey = buildDraftKey({ conversationId, userId, company });

  if (mode != null && mode !== "") {
    const scopedKey = buildDraftKey({ conversationId, userId, company, mode });
    const scoped = readEntry(store, scopedKey);
    if (draftHasPayload(scoped)) return scoped;

    const legacy = readEntry(store, legacyKey);
    if (!draftHasPayload(legacy)) return { ...EMPTY_DRAFT };

    // Promove o legado para o modo atual e remove a chave antiga (evita sumiço
    // ao trocar cliente ↔ anotação no primeiro uso após o deploy).
    store[scopedKey] = {
      text: legacy.text,
      updatedAt: Date.now(),
      ...(legacy.transferTo ? { transferTo: legacy.transferTo } : {}),
      ...(legacy.selectedTemplate ? { selectedTemplate: legacy.selectedTemplate } : {}),
      ...(legacy.pendingVideoInviteButton
        ? { pendingVideoInviteButton: legacy.pendingVideoInviteButton }
        : {}),
    };
    delete store[legacyKey];
    writeStore(pruneStore(store));
    return readEntry(store, scopedKey);
  }

  return readEntry(store, legacyKey);
}

export function loadChatMessageDraft(conversationId, ctx = {}) {
  return loadChatComposerDraft(conversationId, ctx).text;
}

export function saveChatMessageDraft(
  conversationId,
  text,
  {
    userId,
    company,
    mode,
    transferTo,
    selectedTemplate,
    pendingVideoInviteButton,
  } = {}
) {
  if (!conversationId) return;
  const key = buildDraftKey({
    conversationId,
    userId,
    company,
    mode: mode != null && mode !== "" ? mode : undefined,
  });
  const value = typeof text === "string" ? text : "";
  const normalizedTransfer = normalizeDraftTransferTo(transferTo);
  const normalizedTemplate = normalizeDraftSelectedTemplate(selectedTemplate);
  const normalizedVideo = normalizeDraftPendingVideoInvite(pendingVideoInviteButton);
  const store = readStore();

  if (!value.trim()) {
    if (store[key]) {
      delete store[key];
      writeStore(pruneStore(store));
    }
    return;
  }

  store[key] = {
    text: value,
    updatedAt: Date.now(),
    ...(normalizedTransfer ? { transferTo: normalizedTransfer } : {}),
    ...(normalizedTemplate ? { selectedTemplate: normalizedTemplate } : {}),
    ...(normalizedVideo ? { pendingVideoInviteButton: normalizedVideo } : {}),
  };
  // Ao gravar com modo, remove o legado sem modo para não misturar textos.
  if (mode != null && mode !== "") {
    const legacyKey = buildDraftKey({ conversationId, userId, company });
    if (legacyKey !== key && store[legacyKey]) {
      delete store[legacyKey];
    }
  }
  writeStore(pruneStore(store));
}

export function clearChatMessageDraft(conversationId, { userId, company, mode } = {}) {
  if (!conversationId) return;
  if (mode != null && mode !== "") {
    saveChatMessageDraft(conversationId, "", { userId, company, mode });
    return;
  }
  // Sem modo: limpa legado + ambos os modos (envio concluído).
  saveChatMessageDraft(conversationId, "", { userId, company });
  saveChatMessageDraft(conversationId, "", {
    userId,
    company,
    mode: CHAT_DRAFT_MODES.CLIENT,
  });
  saveChatMessageDraft(conversationId, "", {
    userId,
    company,
    mode: CHAT_DRAFT_MODES.INTERNAL,
  });
}
